import { Activity } from "../context/Prestudy/Activity";

export function calculatePrestudyDistrubiton(activities: Activity[], participants: string[]) {
    const totalDurationInMinutes = activities.reduce((acc, activity) => acc + activity.duration, 0);
    const totalDurationInHalfHours = Math.ceil(totalDurationInMinutes / 30) * 30;
    const perParticipantHalfHour = totalDurationInHalfHours / 30;
    const tokensPerParticipant = participants.map(participant => ({ participant, totalPossibleTokens: perParticipantHalfHour }));
    const activityDistributions = activities.map(activity => ({ activityNumber: activity.number, percentage: activity.duration / totalDurationInMinutes }));

    // Make an aggregated list of tokens so there is only one list of tokens left in format {tokens: [{participant: string}]}
    const aggregatedTokens = tokensPerParticipant.flatMap(({ participant, totalPossibleTokens }) => Array(totalPossibleTokens).fill(participant));

    const perActivityTokens = distributeTokens(aggregatedTokens, activityDistributions);

    // For each activity that has 0 tokens, take one from the activity with the most tokens and adjust both
    for (const perActivity of perActivityTokens) {
        if (perActivity.tokens.length === 0) {
            const activityWithMostTokens = perActivityTokens.reduce((acc, curr) => acc.tokens.length > curr.tokens.length ? acc : curr);
            perActivity.tokens.push(activityWithMostTokens.tokens.pop() as string);
        }
    }

    const perParticipantTokensAndActivity = participants.map(participant => {
        const activityTokens = perActivityTokens.map(perActivity => ({ activity: perActivity.activityNumber, tokens: perActivity.tokens.filter(token => token === participant).length })).filter(activity => activity.tokens > 0);
        return { participant, activityTokens };
    });


    const adjustedPerParticipantTokensAndActivity = perParticipantTokensAndActivity.map(participant => {
        const currentTokens = participant.activityTokens.reduce((acc, activity) => acc + activity.tokens, 0);
        const potentialTokens = tokensPerParticipant.find(token => token.participant === participant.participant)!.totalPossibleTokens;
        const tokensToAdd = potentialTokens - currentTokens;
        if (tokensToAdd <= 0) {
            return participant;
        }

        const activityWithLeastTokens = activities.map(activityOrg => {
            const totalDuration = perParticipantTokensAndActivity.flatMap(participant => participant.activityTokens).map(activity => ({ number: activity.activity, duration: activities.find(act => act.number === activity.activity)!.duration, reportedDuration: activity.tokens * 30 })).find(activity => activity.number === activityOrg.number);
            return { ...activityOrg, reportedDuration: totalDuration?.reportedDuration ?? 0 };
        }).reduce((acc, curr) => acc.reportedDuration < curr.reportedDuration ? acc : curr);

        return { participant: participant.participant, activityTokens: [...participant.activityTokens, { activity: activityWithLeastTokens.number, tokens: tokensToAdd }] }
    });


    const difference = participants.map(participant => {
        const usedTokens = adjustedPerParticipantTokensAndActivity.find(participantOrg => participantOrg.participant === participant)!.activityTokens.reduce((acc, activity) => acc + activity.tokens, 0);
        const potentialTokens = tokensPerParticipant.find(token => token.participant === participant)!.totalPossibleTokens;
        return potentialTokens - usedTokens;
    }).reduce((acc, curr) => acc + curr, 0);

    const totalPerActivity: Activity[] = activities.map(activityOrg => {
        const totalTokens = adjustedPerParticipantTokensAndActivity.flatMap(participant => participant.activityTokens).filter(activity => activity.activity === activityOrg.number).reduce((acc, curr) => acc + curr.tokens, 0);
        return { ...activityOrg, reportedDuration: totalTokens * 30 };
    });
    //Do not add any code here
    return {
        totalDurationInMinutes,
        totalDurationInHalfHours,
        difference,
        perParticipantTokensAndActivity: adjustedPerParticipantTokensAndActivity,
        totalPerActivity,
    }
}

export interface PerParticipantTokensAndActivity {
    participant: string;
    activityTokens: ActivityTokens[];
}

interface ActivityTokens {
    activity: number;
    tokens: number;
}

interface ActivityDistribution {
    activityNumber: number;
    percentage: number;
}

interface PerActivityTokens {
    activityNumber: number;
    tokens: string[];
}

function distributeTokens(aggregatedTokensLeft: string[], activityDistributions: ActivityDistribution[]): PerActivityTokens[] {
    const perActivityTokens: PerActivityTokens[] = [];
    const sortedActivityDistributions = activityDistributions.sort((a, b) => b.percentage - a.percentage);
    let tokenIndex = 0;

    for (const distribution of sortedActivityDistributions) {
        const { activityNumber, percentage } = distribution;
        const tokensCount = Math.round(percentage * aggregatedTokensLeft.length);

        const tokens = aggregatedTokensLeft.slice(tokenIndex, tokenIndex + tokensCount);
        tokenIndex += tokensCount;

        perActivityTokens.push({ activityNumber, tokens });
    }

    return perActivityTokens;
}
export function stringSummary(perParticipantTokensAndActivity: PerParticipantTokensAndActivity[], totalPerActivity: Activity[]) {
    const LINK_PREFIX = "https://activity.viskan.com/#Edit_";
    const perActivitySummary = totalPerActivity.map(activity => {
        // [Axxxx](https://activity.viskan.com/#Edit_xxxx): tokens x 30 min
        const link = `[A_${activity.number}](${LINK_PREFIX}${activity.number})`;
        return `${link}:    ${tokensToTime((activity.reportedDuration ?? 0) / 30)}\n`;
    }).join("");

    const perParticipantSummary = perParticipantTokensAndActivity.map(participant => {
        return `**${participant.participant}** ${participant.activityTokens.map(activity => {
            // [Axxxx](https://activity.viskan.com/#Edit_xxxx): tokens x 30 min
            const link = `[A_${activity.activity}](${LINK_PREFIX}${activity.activity})`;
            return `\n${link}:    ${tokensToTime(activity.tokens)}`;
        }).join("")}\n`
    }).join("\n");

    return `### Prestudy summary\n**Combined times per activty**\n${perActivitySummary}\n---------\n${perParticipantSummary}`;
}

const tokensToTime = (tokens: number) => {
    const digits = `${tokens / 2}`;
    const digitsWithDecimals = digits.split(".").length === 1 ? `${digits}.0` : digits;
    return `${digitsWithDecimals} h`;
}

